import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { listAttributeValuesByAttributeID } from "api/Attributes";
import { AttributeContext } from "../AttributeContext";
import { toast } from 'Components/DesignSystem/Toast/useToast'
import ViewMoreButton from "Components/Common/components/ViewMoreButton";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Tag from "Components/DesignSystem/Tag/Tag";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import Search from "Components/DesignSystem/Search/Search";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

const SEARCH_DEBOUNCE_MS = 1000;

/**
 * Read-only "Attribute Values" accordion. Fetch logic is verbatim — including
 * the fetch-on-mount [search] effect and the `setAttValues(0)` quirk in the
 * collapse effect; only the rendering moved onto DS Accordion / Alert / Tag /
 * EmptyState / Search (the legacy CustomSearchBar's 1000 ms debounce now lives
 * here). The ~545 lines of unexported dead code below the old default export
 * (EditSnngleValue, an orphaned EditAttValues copy, a local status strip) are
 * deleted.
 */
const AttributeValuesView = () => {
  const { editingItem, attributeLoading, setAttributeLoading } = useContext(AttributeContext);

  const [loading, setLoading] = useState({
    status: "",
    message: "",
  });
  const [attValues, setAttValues] = useState(null);
  const [attValuesCount, setAttValuesCount] =  useState(0)
  const [collapse, setCollapse] = useState(false);
  const [search, setSearch] = useState("")
  const attrId = useRef(null)

  // DS Search is controlled and has no built-in debounce (the legacy
  // CustomSearchBar carried both, delay 1000).
  const [searchInput, setSearchInput] = useState("");
  /*
    § 288 — the SEARCH half of `loading.status`. That one status drives the view-more
    append too, and a field that spun when the user clicked "View More" would be
    claiming a search nobody asked for. `skip === undefined` is the discriminator the
    fetcher already takes. Presentational — only the field reads it.
  */
  const [searchLoading, setSearchLoading] = useState(false);
  const searchDebounceRef = useRef(null);
  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);
  const handleSearchInput = (value) => {
    const trimmed = value.trimStart();
    setSearchInput(trimmed);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
  };

  function handle_collapse() {
    setCollapse(!collapse);
  }

  const fetchCategoriesData = useCallback((search, skip, reset) => {
    const limit = skip !== undefined ? 10 + skip : 10;
    if(reset) setAttValues(null)
    setAttributeLoading(true)
    attrId.current = editingItem.id
    setLoading({status: "Loading", message: "Loading..."})
    if (skip === undefined) setSearchLoading(true)
    listAttributeValuesByAttributeID(editingItem.id, limit, skip, search)
    .then(({count, attribute_values}) => {
      setAttValues((prev) => {
        return (prev || !reset) ? [...prev, ...attribute_values] : attribute_values
      })
      setAttValuesCount(count)
      setLoading({
        status: "Success",
        message: ""
      })
    })
    .catch(err => {
      toast.error(err.message)
      setLoading({
        status: "Error",
        message: err.message
      })
    }).finally(()=>{ setAttributeLoading(false); setSearchLoading(false) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchCategoriesData(search, undefined, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    setAttValues([])
    setAttValuesCount(0)
  }, [editingItem.id, search])

  useEffect(() => {
    if(!collapse || attrId.current === editingItem.id) return
    setAttValues([])
    setAttValues(0)
    fetchCategoriesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse])


  return (
    <Accordion
      title="Attribute Values"
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-attributevaluesview-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <div className="tw-flex tw-justify-end tw-pt-2">
          <Search
            size="sm"
            className="tw-w-60"
            placeholder="Search Attribute Values"
            ariaLabel="Search attribute values"
            data-testid="attribute-values-view-search-input"
            value={searchInput}
            onChange={handleSearchInput}
            /* § 288 — the search-only flag plus the 1000ms debounce in front of it.
               The view-more append keeps its own spinner below the table. */
            loading={searchLoading || searchInput !== search}
            loadingLabel="Searching values"
            disabled={attributeLoading}
          />
        </div>

        {/* A view-more append must not blank the list (user-reported 2026-09-01 —
            the legacy strip swap made "view more" feel like a page reload). While
            values are already on screen, the fetch renders as an inline spinner in
            the button's place; only a load with nothing to show yet gets skeleton
            chips. `appending` is derived, not new state — the fetch logic is
            untouched. */}
        {loading.status === "Loading" && !(attValues && attValues.length > 0) && (
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-pt-3"
            data-testid="attribute-values-view-skeleton"
          >
            {[72, 96, 56, 88, 64, 80].map((w, i) => (
              <Skeleton key={i} variant="circle" width={`${w}px`} height="26px" />
            ))}
          </div>
        )}

        {loading.status === "Error" && (
          <Alert tone="danger" live className="tw-mt-3" data-testid="attribute-values-view-status">
            {loading.message}
          </Alert>
        )}

        {(loading.status === "Success" ||
          (loading.status === "Loading" && attValues && attValues.length > 0)) &&
          attValues && (
          <div className="tw-pt-3">
            {attValues && attValues.length > 0 ? (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                {attValues.map((item, index) => {
                  return (
                    <Tag key={index} label={item.value} variant="meta" />
                  );
                })}

                {loading.status === "Loading" ? (
                  <span
                    className="tw-inline-flex tw-items-center tw-px-2"
                    data-testid="attribute-values-view-loading-more"
                  >
                    <Spinner size="sm" label="Loading more values" />
                  </span>
                ) : (
                  <ViewMoreButton
                    condition = {attValues.length < attValuesCount}
                    state = {attValues.length >= attValuesCount}
                    handleClick = {() => fetchCategoriesData(search, attValues.length)}
                  />
                )}

              </div>
            ) : (
              <div className="tw-flex tw-justify-center tw-py-3">
                <EmptyState
                  size="sm"
                  title="Attribute Values Not Found"
                  data-testid="attribute-values-view-empty"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Accordion>
  );
};

export default AttributeValuesView;
